import { ContextTagPlot } from 'features/sentinelDesign/ContextTagPlot'
import {
  Badge,
  Callout,
  Hint,
  List,
  num,
  Panel,
  PanelTitle,
  percent,
  Row,
  StatTable,
} from 'features/sentinelDesign/designStyles'
import { MonthCoverageStrip } from 'features/sentinelDesign/MonthCoverageStrip'
import type {
  SentinelDesignContext,
  SentinelDesignContextTag,
} from 'features/sentinelDesign/types'
import { CollapsibleSection } from 'features/timeSeries/experiment/CollapsibleSection'
import { FC } from 'react'
import styled from 'styled-components'

const TagCard = styled.div`
  display: grid;
  grid-template-columns: minmax(18rem, 2fr) minmax(14rem, 1fr);
  gap: 0.75rem;
  padding: 0.7rem;
  border: 1px solid ${({ theme }) => theme.forecast.border};
  border-radius: 0.5rem;
  background: ${({ theme }) => theme.forecast.background2};

  @media (max-width: 70rem) {
    grid-template-columns: 1fr;
  }
`

const TagColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  min-width: 0;
`

const TagName = styled.div`
  font-size: 0.8rem;
  font-weight: 600;
  overflow-wrap: anywhere;
`

const TagStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
`

const roleTone = (tag: SentinelDesignContextTag) =>
  tag.required ? 'warning' : 'neutral'

/** The stats rows worth showing, skipping whatever the bundle didn't report. */
const statRows = (tag: SentinelDesignContextTag): Array<[string, string]> => {
  const stats = tag.stats
  if (!stats) return []
  const rows: Array<[string, string]> = []
  if (typeof stats.rows === 'number') rows.push(['Rows', String(stats.rows)])
  if (typeof stats.observed === 'number') {
    rows.push(['Observed', String(stats.observed)])
  }
  rows.push(['Observed (window)', percent(stats.observed_frac, 1)])
  if (stats.mean !== null && stats.mean !== undefined) {
    rows.push(['Mean', num(stats.mean)])
  }
  if (stats.median !== null && stats.median !== undefined) {
    rows.push(['Median', num(stats.median)])
  }
  if (stats.min !== null && stats.min !== undefined) {
    rows.push(['Min / max', `${num(stats.min)} / ${num(stats.max)}`])
  }
  if (typeof stats.native_spacing_seconds === 'number') {
    rows.push(['Native spacing', `${stats.native_spacing_seconds}s`])
  }
  if (typeof stats.longest_gap_periods === 'number') {
    rows.push(['Longest gap', `${stats.longest_gap_periods} periods`])
  }
  if (stats.first_ts || stats.last_ts) {
    rows.push([
      'First / last',
      `${stats.first_ts?.slice(0, 10) ?? '—'} → ${stats.last_ts?.slice(0, 10) ?? '—'}`,
    ])
  }
  return rows
}

export interface ContextBundleViewProps {
  context: SentinelDesignContext
  fetchTagPlot: (tag: string) => Promise<string | null>
}

/**
 * The built context bundle: what the design agent is about to look at. Each tag
 * gets its composite plot next to its stats, with per-month coverage given the
 * prominent slot.
 */
export const ContextBundleView: FC<ContextBundleViewProps> = ({
  context,
  fetchTagPlot,
}) => {
  const gadsEvents = context.gads_events ?? []
  const catchable = gadsEvents.filter((event) => event.catchable)

  return (
    <Panel aria-label='Context bundle'>
      <Row>
        <PanelTitle>Context bundle</PanelTitle>
        <Badge>{context.tags.length} tags</Badge>
        <Badge>{context.interval_minutes} min grid</Badge>
        <Badge>
          {context.window.start.slice(0, 10)} →{' '}
          {context.window.end.slice(0, 10)}
        </Badge>
        {context.context_hash && (
          <Badge title='Context hash'>
            #{context.context_hash.slice(0, 8)}
          </Badge>
        )}
      </Row>

      {context.pid ? (
        <Callout>
          <strong>P&amp;ID:</strong>{' '}
          {context.pid.chosen_name ?? context.pid.chosen_ref ?? 'unnamed sheet'}
          {context.pid.reason ? ` — ${context.pid.reason}` : ''}
        </Callout>
      ) : (
        <Callout $tone='warning'>
          No P&amp;ID reached the design agent. It will plan from the tag list
          and stats alone, without the process topology.
        </Callout>
      )}

      {context.plant_context_tags?.length ? (
        <Hint>
          Plant context tags in scope: {context.plant_context_tags.join(', ')}
        </Hint>
      ) : null}

      <CollapsibleSection
        sectionKey='sentinel-design-context-tags'
        title={`Tags (${context.tags.length})`}>
        <TagStack>
          {context.tags.map((tag) => (
            <TagCard key={tag.tag}>
              <TagColumn>
                <TagName>{tag.tag}</TagName>
                <Row>
                  {tag.role_hint && <Badge>{tag.role_hint}</Badge>}
                  {tag.required && (
                    <Badge $tone={roleTone(tag)}>required</Badge>
                  )}
                  {tag.measurement_class && (
                    <Badge>{tag.measurement_class}</Badge>
                  )}
                  {tag.unit && <Badge>{tag.unit}</Badge>}
                  {tag.slot_key && (
                    <Badge title='Fill slot'>{tag.slot_key}</Badge>
                  )}
                  {tag.instrument_id && (
                    <Badge title='Instrument'>{tag.instrument_id}</Badge>
                  )}
                </Row>
                {tag.label && <Hint>{tag.label}</Hint>}
                <ContextTagPlot tag={tag.tag} fetchPlot={fetchTagPlot} />
              </TagColumn>
              <TagColumn>
                <MonthCoverageStrip months={tag.stats?.by_month} />
                {statRows(tag).length > 0 ? (
                  <StatTable>
                    <tbody>
                      {statRows(tag).map(([label, value]) => (
                        <tr key={label}>
                          <th scope='row'>{label}</th>
                          <td>{value}</td>
                        </tr>
                      ))}
                    </tbody>
                  </StatTable>
                ) : (
                  <Hint>No stats were reported for this tag.</Hint>
                )}
              </TagColumn>
            </TagCard>
          ))}
        </TagStack>
      </CollapsibleSection>

      {gadsEvents.length > 0 && (
        <CollapsibleSection
          sectionKey='sentinel-design-context-gads'
          defaultOpen={false}
          title={`GADS events in window (${gadsEvents.length}${
            catchable.length ? `, ${catchable.length} catchable` : ''
          })`}>
          <Hint>
            Catchable events are the ones a residual monitor could plausibly
            have flagged — they are the yardstick for whether this model is
            worth running.
          </Hint>
          <StatTable>
            <thead>
              <tr>
                <th>Start</th>
                <th>End</th>
                <th>Code</th>
                <th>Class</th>
                <th>Description</th>
                <th>Catchable</th>
              </tr>
            </thead>
            <tbody>
              {gadsEvents.map((event, index) => (
                <tr key={`${event.start}-${event.code ?? index}`}>
                  <td>{event.start.slice(0, 10)}</td>
                  <td>{event.end.slice(0, 10)}</td>
                  <td>{event.code ?? '—'}</td>
                  <td>{event.event_class ?? '—'}</td>
                  <td>{event.description ?? '—'}</td>
                  <td>
                    {event.catchable ? (
                      <Badge $tone='warning'>yes</Badge>
                    ) : (
                      'no'
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </StatTable>
        </CollapsibleSection>
      )}

      {gadsEvents.length === 0 && (
        <Hint>
          No GADS events overlap this window, so there is no recorded outage to
          check the model against.
        </Hint>
      )}
    </Panel>
  )
}

/** The eligibility verdict, rendered as its reasons rather than a failure. */
export const EligibilityNotice: FC<{
  eligibility: SentinelDesignContext['eligibility']
  /** Paste-path per-tag resolution, so unresolved tags read tag-by-tag. */
  tagResolution?: SentinelDesignContext['tag_resolution']
}> = ({ eligibility, tagResolution }) => {
  if (eligibility.ok) return null
  const unresolved = (tagResolution ?? []).filter(
    (entry) => entry.status !== 'resolved'
  )
  return (
    <Callout $tone='error' role='alert'>
      <strong>This instance cannot be modelled yet.</strong>
      {eligibility.reasons.length > 0 ? (
        <List>
          {eligibility.reasons.map((reason) => (
            <li key={reason}>{reason}</li>
          ))}
        </List>
      ) : (
        <Hint>The backend reported no reason.</Hint>
      )}
      {unresolved.length > 0 && (
        <>
          <Hint>Pasted tags that did not resolve to a live datapoint:</Hint>
          <List>
            {unresolved.map((entry) => (
              <li key={entry.raw}>
                <code>{entry.raw}</code> — {entry.error ?? entry.status}
              </li>
            ))}
          </List>
        </>
      )}
    </Callout>
  )
}
